// environmentAssetId — the SERVER's id for an environment, from either shape of
// "environment asset" the app passes around.
//
// Two shapes reach applyEnvironment and they carry different ids:
//   - an assetRegistry record, whose `id` is NAMESPACED by kind
//     (`environment:<uuid>`, assetRegistry.js) with the server id in `rawId`
//   - a raw server row (CustomAssetManager / the org list), whose `id` IS the
//     server id
//
// The prefixed one is not an id the asset-manager has ever heard of. Handed to
// `GET /api/org/environments/{id}` it answers 400 `invalid asset id`, which
// nulls the environment's whole `files` map — so every baked artifact of a
// fully baked environment reads as absent and the NAVMESH panel concludes the
// environment "has no .ply source". Reported against Cliff_Level (2026-09-01),
// whose .ply the server lists and serves; the bake had failed for an unrelated
// reason and this message sent the diagnosis somewhere else entirely.
//
// It is a decode of OUR OWN client-side naming scheme, not a derivation of a
// server value — the id is still the one the server stated, just unwrapped. One
// module owns the unwrapping so no call site has to know the scheme, and so a
// second namespaced kind can never quietly reach an endpoint.
//
// No imports on purpose: applyEnvironment.js is loaded directly by node:test,
// and assetRegistry.js (where the prefix is minted) is unloadable there — it
// reaches CustomAssetManager through an extensionless specifier only Vite
// resolves.

// The prefixes assetRegistry actually mints (the `id:` template literals in
// assetRegistry.js), spelled out rather than matched as a generic `word:`
// shape. A generic one also matches a URL scheme, and rewrote `https://host/x`
// to `//host/x` — silently, with a plausible-looking result. An id is only
// unwrapped when it names a kind we know we wrapped.
const REGISTRY_KINDS = [
  'anim', 'audio', 'blueprint_asset', 'environment', 'material', 'prefab', 'rig', 'texture',
]
const NAMESPACED = new RegExp(`^(${REGISTRY_KINDS.join('|')}):(.+)$`)

/**
 * The server-side id of an environment asset, or null when it has none.
 *
 * `rawId` is authoritative when present (the registry states it outright);
 * otherwise a namespaced `id` is unwrapped and a plain one is returned as is.
 */
export function environmentAssetId(asset) {
  if (!asset) return null
  if (asset.rawId) return String(asset.rawId)
  const id = asset.id
  if (!id) return null
  const m = NAMESPACED.exec(String(id))
  return m ? m[2] : String(id)
}

/**
 * True when `id` is a registry-namespaced id rather than a server one — for the
 * call sites that want to REFUSE one rather than unwrap it.
 */
export function isNamespacedAssetId(id) {
  return typeof id === 'string' && NAMESPACED.test(id)
}
